import { createFileRoute } from "@tanstack/react-router";
import { breadcrumbSchema, webPageSchema } from "@/lib/schema";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Pal Stars" },
      { name: "description", content: "How Pal Stars handles cookies, localStorage, analytics and contact form data on our Palworld fan wiki." },
      { property: "og:title", content: "Privacy Policy — Pal Stars" },
      { property: "og:description", content: "How Pal Stars handles cookies, localStorage, analytics and contact form data." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/privacy" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/privacy" }],
    scripts: [breadcrumbSchema([["Home", "/"], ["Privacy Policy", "/privacy"]]), webPageSchema({ name: "Privacy Policy", description: "How Pal Stars handles analytics, cookies and account data.", path: "/privacy" })],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 py-12 prose prose-invert">
      <h1 className="text-3xl sm:text-4xl font-bold">Privacy Policy</h1>
      <p className="mt-2 text-sm text-muted-foreground">Last updated: July 27, 2026</p>

      <p className="mt-6">
        Pal Stars ("we", "us", "the site") is an unofficial fan-made Palworld wiki. This page explains what
        limited information we collect when you visit palstars.com and how we use it.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">1. Information we collect</h2>
      <ul className="mt-2 list-disc pl-6 space-y-1">
        <li><b>Local browser storage.</b> Favorites, recently viewed Pals, theme choice, deck builds are stored only in your own browser via <code>localStorage</code>. They never reach our servers.</li>
        <li><b>Contact form.</b> If you email us through the contact page, we receive the name, email address and message you submit so we can reply.</li>
        <li><b>Server logs.</b> Our hosting provider records standard request metadata (IP address, user agent, timestamp) for security and abuse prevention.</li>
        <li><b>Cookies.</b> We use a small number of first-party cookies for essential functionality (session, cookie-consent state). We do not sell your data.</li>
      </ul>

      <h2 className="mt-8 text-xl font-semibold text-foreground">2. How we use it</h2>
      <p className="mt-2">To operate the site, remember your preferences, respond to contact requests, and monitor for abuse. We do not build advertising profiles.</p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">3. Third-party embeds</h2>
      <p className="mt-2">
        Pages that embed third-party content (YouTube trailers, Google Fonts, external guide links) may set their
        own cookies when you interact with them. Those services are governed by their own privacy policies.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">4. Your choices</h2>
      <p className="mt-2">
        You can clear favorites, history and other stored preferences at any time by clearing site data in your
        browser settings. You can decline non-essential cookies from the banner shown on your first visit.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">5. Affiliate links &amp; marketplace data</h2>
      <p className="mt-2">
        Pal Stars participates in affiliate programs, including but not limited to the eBay Partner Network. When you
        click a merchant link and make a purchase, this can result in this site earning a commission. Outbound clicks are
        logged in aggregate (the target card or product, a non-personal tracking sub-id, and a timestamp) so commissions
        can be attributed; we do not receive your name, address or payment details from these programs. Marketplace
        prices shown on the site come from validated third-party marketplace feeds. eBay and other merchants may set their
        own cookies once you land on their site, governed by their own privacy policies.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">6. Accounts &amp; analytics</h2>
      <p className="mt-2">
        If you create an account, our backend provider stores your email address, an authentication record and your
        in-site progress (packs, binder, spins, badges). We use Google Analytics with IP anonymisation to measure
        aggregate traffic; you can block it with any standard tracker blocker or by declining non-essential cookies.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">7. Legal basis, retention &amp; your rights</h2>
      <p className="mt-2">
        We process data on the basis of legitimate interest (site security, aggregate analytics, affiliate attribution)
        and consent (non-essential cookies). Contact messages and click logs are retained only as long as needed for
        support and commission reconciliation. Depending on where you live — including under the GDPR and the CCPA/CPRA —
        you may request access to, correction of, or deletion of your data, and you may opt out of any sale or sharing of
        personal information. We do not sell or share personal information. To exercise a right, email us via the contact
        page and we will respond within the period required by law.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">8. Children</h2>
      <p className="mt-2">Pal Stars is not directed at children under 13. We do not knowingly collect data from children.</p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">9. Contact</h2>
      <p className="mt-2">Questions about this policy? Use the contact page linked in the footer.</p>

      <p className="mt-8 text-xs text-muted-foreground">
        Pal Stars is an unofficial fan project and is not affiliated with, endorsed by, or approved by Pocketpair Inc.
      </p>
    </article>
  );
}
